import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import {
  fetchWallet, fetchHistory, fetchPacks, fetchPlans, fetchPayments, fetchSubscription,
  createCheckout, subscribe, cancelSubscription, formatEur, PLAN_NAMES,
} from "../lib/billing";
import {
  Wallet, Receipt, Plus, ShieldCheck, ArrowUpRight, ArrowDownRight, CreditCard,
  Loader2, CheckCircle2, Clock, XCircle, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

export default function Billing() {
  const { t } = useT();
  const [w, setW] = useState(null);
  const [history, setHistory] = useState([]);
  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [sub, setSub] = useState(null);
  const [buying, setBuying] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    Promise.all([fetchWallet(), fetchHistory(), fetchPacks(), fetchPlans(), fetchPayments(), fetchSubscription()])
      .then(([wd, h, p, pl, pay, s]) => {
        setW(wd); setHistory(h); setPacks(p); setPlans(pl); setPayments(pay); setSub(s.subscription);
      })
      .catch(() => {});
  }, []);

  if (!w) return <DashboardLayout title={t("Billing")}><div className="text-[#94A3B8]">{t("Loading…")}</div></DashboardLayout>;

  const buyPack = async (packId) => {
    setBuying(packId);
    try {
      const { url } = await createCheckout(packId);
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("Could not start checkout."));
      setBuying(null);
    }
  };

  const subscribePlan = async (planId) => {
    setSubscribing(planId);
    try {
      const { url } = await subscribe(planId);
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("Could not start checkout."));
      setSubscribing(null);
    }
  };

  const cancelSub = async () => {
    setCancelling(true);
    try {
      const r = await cancelSubscription();
      setSub(r.subscription);
      toast.success(t("Subscription set to cancel at period end"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Could not cancel"));
    } finally { setCancelling(false); }
  };

  return (
    <DashboardLayout title={t("Billing & credits")}>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <PlanOverviewCard w={w} sub={sub} cancelling={cancelling} onCancel={cancelSub} t={t} />
          <SubscriptionPlansCard plans={plans} currentPlanId={w.plan.plan_id} sub={sub} subscribing={subscribing} onSubscribe={subscribePlan} t={t} />
          <UsageHistoryCard history={history} t={t} />
          <PaymentHistoryCard payments={payments} t={t} />
        </div>
        <div className="space-y-6">
          <WalletCard wallet={w.wallet} t={t} />
          <CreditPacksCard packs={packs} buying={buying} onBuy={buyPack} t={t} />
          <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-5 text-xs text-[#475569] flex gap-2">
            <ShieldCheck className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
            <p>{t("Konzenzo provides AI-assisted analysis to support — not replace — qualified human review for tax, legal and financial matters. Final decisions remain the sole responsibility of you and your organization.")}</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

const ActiveSubscriptionBox = ({ sub, cancelling, onCancel, t }) => (
  <div data-testid="active-subscription-box" className="rounded-lg border border-[#10B981]/40 bg-[#10B981]/5 p-4">
    <p className="font-mono-kz text-xs text-[#10B981] tracking-widest font-bold flex items-center gap-1.5">
      <BadgeCheck className="w-3.5 h-3.5" /> {t("ACTIVE SUBSCRIPTION")}
    </p>
    <p className="text-sm text-[#0B1B3D] font-bold mt-1">{PLAN_NAMES[sub.plan_id] || sub.plan_id}</p>
    <p className="text-xs text-[#475569] mt-0.5">
      {sub.cancel_at_period_end ? t("Cancels at period end") : t("Renews")}{" "}
      {!sub.cancel_at_period_end && sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : ""}
    </p>
    {!sub.cancel_at_period_end ? (
      <button data-testid="cancel-subscription-btn" onClick={onCancel} disabled={cancelling}
        className="mt-2 text-xs font-bold text-[#EF4444] hover:underline disabled:opacity-50">
        {cancelling ? t("Cancelling…") : t("Cancel subscription")}
      </button>
    ) : (
      <p className="mt-2 text-xs text-[#94A3B8]">{t("Your subscription will end at the current period end. You keep your credits until then.")}</p>
    )}
  </div>
);

const PlanOverviewCard = ({ w, sub, cancelling, onCancel, t }) => {
  const plan = w.plan;
  const used = w.used_this_period;
  const total = plan.included_monthly_credits || 0;
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">{t("CURRENT PLAN")}</p>
          <p className="font-display font-black text-3xl text-[#0B1B3D] mt-1">{plan.name}</p>
          <p className="text-sm text-[#475569] mt-1">
            {plan.price_eur != null ? `${formatEur(plan.price_eur)}/mo` : t("Custom pricing")} ·{" "}
            {plan.included_users || "—"} {plan.included_users === 1 ? t("user") : t("users")}
          </p>
        </div>
        {sub && <ActiveSubscriptionBox sub={sub} cancelling={cancelling} onCancel={onCancel} t={t} />}
      </div>
      <div className="mt-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-mono-kz text-xs text-[#94A3B8] tracking-widest font-bold uppercase">{t("Monthly credit usage")}</span>
          <span className="font-mono-kz text-xs text-[#0B1B3D] font-bold">{used} / {total || "∞"} {t("used")}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#10B981]" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-2 text-xs text-[#94A3B8] font-mono-kz">
          {t("Resets")} {new Date(w.workspace.current_period_end).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

const SubscriptionPlansCard = ({ plans, currentPlanId, sub, subscribing, onSubscribe, t }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="subscription-plans">
    <h3 className="font-display font-extrabold text-[#0B1B3D] text-lg">{t("Subscription plans")}</h3>
    <p className="text-sm text-[#475569] mt-1">{t("Monthly recurring billing via Stripe. Cancel anytime.")}</p>
    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {plans.filter((p) => p.plan_id !== "free").map((p) => (
        <PlanCard key={p.plan_id} plan={p} isCurrent={p.plan_id === currentPlanId} sub={sub}
          isSubmitting={subscribing === p.plan_id} disabled={!!subscribing || !!sub} onSubscribe={onSubscribe} t={t} />
      ))}
    </div>
  </div>
);

const PlanCard = ({ plan: p, isCurrent, isSubmitting, disabled, onSubscribe, t }) => (
  <div className={`rounded-xl border p-4 flex flex-col ${isCurrent ? "border-[#10B981] ring-2 ring-[#10B981]/20" : "border-slate-200"}`}>
    <p className="font-display font-extrabold text-[#0B1B3D]">{PLAN_NAMES[p.plan_id] || p.name}</p>
    <p className="font-display font-black text-2xl text-[#0B1B3D] mt-1">
      {p.price_eur != null ? formatEur(p.price_eur) : "—"}<span className="text-sm font-bold text-[#94A3B8]">{p.price_eur != null ? "/mo" : ""}</span>
    </p>
    <p className="text-xs text-[#94A3B8] font-mono-kz mt-1">
      {p.included_monthly_credits ? `${p.included_monthly_credits} ${t("cr/mo")}` : t("Custom credits")}
    </p>
    <div className="mt-auto pt-4">
      {isCurrent ? (
        <span className="inline-flex w-full justify-center px-3 py-2 rounded-lg bg-[#10B981]/10 text-[#10B981] text-sm font-bold font-display">{t("Current plan")}</span>
      ) : p.plan_id === "enterprise" ? (
        <a href="mailto:hello@konzenzo.com" className="inline-flex w-full justify-center px-3 py-2 rounded-lg border border-slate-200 text-[#0B1B3D] text-sm font-bold font-display hover:border-[#0EA5E9]">{t("Contact us")}</a>
      ) : (
        <button data-testid={`subscribe-${p.plan_id}`} onClick={() => onSubscribe(p.plan_id)} disabled={disabled}
          className="inline-flex w-full justify-center items-center gap-2 px-3 py-2 rounded-lg bg-[#0B1B3D] text-white text-sm font-bold font-display hover:bg-[#1E3A8A] disabled:opacity-40">
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {t("Subscribe")}
        </button>
      )}
    </div>
  </div>
);

const UsageHistoryCard = ({ history, t }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="credit-history">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display font-extrabold text-[#0B1B3D] text-lg flex items-center gap-2">
        <Receipt className="w-5 h-5 text-[#0EA5E9]" /> {t("Usage history")}
      </h3>
      <span className="font-mono-kz text-xs text-[#94A3B8] tracking-widest">{history.length} {t("ENTRIES")}</span>
    </div>
    {history.length === 0 ? (
      <p className="text-sm text-[#94A3B8] py-6 text-center border border-dashed border-slate-200 rounded-lg">{t("No transactions yet.")}</p>
    ) : (
      <div className="divide-y divide-slate-100">
        {history.map((tx) => (
          <div key={tx.transaction_id} className="py-3 flex items-center justify-between text-sm">
            <div>
              <p className="font-bold text-[#0B1B3D]">{t(labelFor(tx))}</p>
              <p className="text-xs text-[#94A3B8] font-mono-kz">{new Date(tx.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`font-mono-kz font-bold ${tx.amount < 0 ? "text-[#EF4444]" : "text-[#10B981]"}`}>
                {tx.amount > 0 ? "+" : ""}{tx.amount} cr
              </span>
              {tx.amount < 0 ? <ArrowDownRight className="w-4 h-4 text-[#EF4444]" /> : <ArrowUpRight className="w-4 h-4 text-[#10B981]" />}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PaymentHistoryCard = ({ payments, t }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="payment-history">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display font-extrabold text-[#0B1B3D] text-lg flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-[#0EA5E9]" /> {t("Payment history")}
      </h3>
      <span className="font-mono-kz text-xs text-[#94A3B8] tracking-widest">{payments.length} {t("PAYMENTS")}</span>
    </div>
    {payments.length === 0 ? (
      <p className="text-sm text-[#94A3B8] py-6 text-center border border-dashed border-slate-200 rounded-lg">{t("No payments yet.")}</p>
    ) : (
      <div className="divide-y divide-slate-100">
        {payments.map((p) => (
          <div key={p.stripe_session_id} data-testid={`payment-row-${p.stripe_session_id}`} className="py-3 flex items-center justify-between text-sm">
            <div className="min-w-0">
              <p className="font-bold text-[#0B1B3D]">{p.pack_name}</p>
              <p className="text-xs text-[#94A3B8] font-mono-kz truncate max-w-[260px]">{p.stripe_session_id}</p>
              <p className="text-xs text-[#94A3B8] font-mono-kz">{new Date(p.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-display font-extrabold text-[#0B1B3D]">€{p.amount_eur}</span>
              <PaymentStatusPill status={p.status} credited={p.credited} t={t} />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const WalletCard = ({ wallet, t }) => (
  <div className="bg-[#0B1B3D] text-white rounded-xl p-6">
    <Wallet className="w-6 h-6 text-[#10B981]" />
    <p className="mt-4 font-mono-kz text-xs text-[#10B981] tracking-widest font-bold">{t("CURRENT BALANCE")}</p>
    <p data-testid="wallet-total-balance" className="font-display font-black text-5xl mt-1">{wallet.total_balance}</p>
    <p className="text-sm text-slate-300 mt-1">{t("credits available")}</p>
    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
      <div className="border border-white/15 rounded-lg p-3">
        <p className="font-mono-kz text-xs text-slate-400">{t("Monthly")}</p>
        <p className="font-bold text-white mt-0.5">{wallet.monthly_credit_balance}</p>
      </div>
      <div className="border border-white/15 rounded-lg p-3">
        <p className="font-mono-kz text-xs text-slate-400">{t("Purchased")}</p>
        <p data-testid="wallet-purchased-balance" className="font-bold text-white mt-0.5">{wallet.purchased_credit_balance}</p>
      </div>
    </div>
  </div>
);

const CreditPacksCard = ({ packs, buying, onBuy, t }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="font-display font-extrabold text-[#0B1B3D] text-lg flex items-center gap-2">
      <Plus className="w-5 h-5 text-[#0EA5E9]" /> {t("Buy credit packs")}
    </h3>
    <div className="mt-4 space-y-2">
      {packs.map((p) => (
        <button key={p.pack_id} data-testid={`buy-${p.pack_id}`} onClick={() => onBuy(p.pack_id)} disabled={!!buying}
          className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-left disabled:opacity-60 disabled:cursor-wait">
          <div>
            <p className="font-bold text-[#0B1B3D] text-sm">{p.name}</p>
            <p className="text-xs text-[#94A3B8] font-mono-kz">
              €{(p.price_eur / p.credits).toFixed(2)} / cr · {t("12-month validity")}
            </p>
          </div>
          <span className="font-display font-extrabold text-[#0B1B3D] inline-flex items-center gap-2">
            {buying === p.pack_id && <Loader2 className="w-4 h-4 animate-spin text-[#0EA5E9]" />}
            {formatEur(p.price_eur)}
          </span>
        </button>
      ))}
    </div>
    <p className="mt-3 text-xs text-[#94A3B8] font-mono-kz">{t("VAT calculated by Stripe Tax at checkout · Secure payment via Stripe")}</p>
  </div>
);

function PaymentStatusPill({ status, credited, t }) {
  if (status === "paid" && credited) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-bold font-mono-kz">
        <CheckCircle2 className="w-3 h-3" /> {t("PAID")}
      </span>
    );
  }
  if (status === "expired" || status === "cancelled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-bold font-mono-kz">
        <XCircle className="w-3 h-3" /> {status.toUpperCase()}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-bold font-mono-kz">
      <Clock className="w-3 h-3" /> {t("PENDING")}
    </span>
  );
}

function labelFor(tx) {
  const map = {
    monthly_allocation: "Monthly plan credits",
    consumption: "Konzenzo session",
    refund: "Refund (failed session)",
    purchase: "Credit pack purchase",
    auto_top_up: "Auto top-up",
    expiration: "Credit expiration",
    manual_adjustment: "Manual adjustment",
  };
  return map[tx.transaction_type] || tx.transaction_type;
}
